import React, { useState } from 'react';
import { X, Package, Shield, Factory, ShoppingCart, ShoppingBag, UserCheck, Award, Truck, BarChart3, DollarSign, Layers, Users, Warehouse, Box, Building2 } from 'lucide-react';

interface ModuleOption {
  id: number;
  name: string;
  displayName: string;
  description: string;
  category?: string;
}

interface CompanyOption {
  tenantId: number;
  name: string;
}

export interface TeamMemberFormValues {
  name: string;
  email: string;
  phone: string;
  password: string;
  permissions: { moduleId: number; canView: boolean; canCreate: boolean; canUpdate: boolean; canDelete: boolean }[];
  companyAccess: { tenantId: number; canWrite: boolean }[];
}

export interface TeamMemberFormInitial {
  name: string;
  email: string;
  phone: string;
  permissions: { moduleId: number }[];
  companyAccess: { tenantId: number; canWrite: boolean }[];
}

interface CreateTeamMemberModalProps {
  modules: ModuleOption[];
  companies: CompanyOption[];
  initial?: TeamMemberFormInitial | null;
  onClose: () => void;
  onSubmit: (values: TeamMemberFormValues) => void;
  submitting?: boolean;
}

const MODULE_ICONS: Record<string, any> = {
  'chip-management': Package,
  'gps-management': Shield,
  machines: Factory,
  purchases: ShoppingCart,
  sales: ShoppingBag,
  clients: UserCheck,
  qualities: Award,
  stock: Warehouse,
  'yarn-outward': Truck,
  'yarn-inward': Box,
  'yarn-dashboard': BarChart3,
  'taka-inventory': Box,
  'beam-master': Box,
  production: BarChart3,
  salary: DollarSign,
  beams: Layers,
  employees: Users,
};

const INVENTORY_MODULE_NAMES = new Set([
  'stock',
  'yarn-inward',
  'yarn-dashboard',
  'yarn-outward',
  'taka-inventory',
  'beam-master',
]);

// Team-member create/edit form: company name/email/phone/password are not asked (this
// isn't a new company), instead a module picker identical to CreateCompanyModal's, plus
// a per-company access list — toggle a company on, then choose read-write or read-only.
const CreateTeamMemberModal: React.FC<CreateTeamMemberModalProps> = ({
  modules,
  companies,
  initial,
  onClose,
  onSubmit,
  submitting,
}) => {
  const [name, setName] = useState(initial?.name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [password, setPassword] = useState('');
  const [selectedModuleIds, setSelectedModuleIds] = useState<Set<number>>(
    new Set((initial?.permissions || []).map((p) => p.moduleId))
  );
  const [companyAccess, setCompanyAccess] = useState<Map<number, boolean>>(
    new Map((initial?.companyAccess || []).map((g) => [g.tenantId, g.canWrite]))
  );

  const toggleModule = (id: number) => {
    setSelectedModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleCompany = (tenantId: number) => {
    setCompanyAccess((prev) => {
      const next = new Map(prev);
      if (next.has(tenantId)) next.delete(tenantId); else next.set(tenantId, false);
      return next;
    });
  };

  const setCompanyWrite = (tenantId: number, canWrite: boolean) => {
    setCompanyAccess((prev) => {
      const next = new Map(prev);
      next.set(tenantId, canWrite);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      email,
      phone,
      password,
      permissions: Array.from(selectedModuleIds).map((moduleId) => ({
        moduleId,
        canView: true,
        canCreate: true,
        canUpdate: true,
        canDelete: true,
      })),
      companyAccess: Array.from(companyAccess.entries()).map(([tenantId, canWrite]) => ({ tenantId, canWrite })),
    });
  };

  const groups: { key: string; label: string; items: ModuleOption[] }[] = [
    { key: 'main', label: 'Main Modules', items: modules.filter((m) => m.category === 'main') },
    { key: 'inventory', label: 'Inventory', items: modules.filter((m) => INVENTORY_MODULE_NAMES.has(m.name)) },
    {
      key: 'textile',
      label: 'Textile Modules',
      items: modules.filter((m) => m.category !== 'main' && !INVENTORY_MODULE_NAMES.has(m.name)),
    },
  ].filter((g) => g.items.length > 0);

  const renderModuleRow = (module: ModuleOption) => {
    const Icon = MODULE_ICONS[module.name] || Package;
    const checked = selectedModuleIds.has(module.id);
    return (
      <div key={module.id} className="flex items-center justify-between gap-3 px-3 py-3 min-h-[52px] rounded-lg hover:bg-gray-50">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">{module.displayName}</p>
            <p className="text-xs text-gray-500">{module.description}</p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={`${checked ? 'Revoke' : 'Grant'} access to ${module.displayName}`}
          onClick={() => toggleModule(module.id)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
            checked ? 'bg-blue-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{initial ? 'Edit Team Member' : 'Create Team Member'}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 123-4567"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder={initial ? 'Leave blank to keep current password' : ''}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required={!initial} />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900">Companies</h3>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Choose which of your companies this person can access, and whether they can make changes.</p>
              {companies.length === 0 ? (
                <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">
                  You haven't created any companies yet.
                </p>
              ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {companies.map((c) => {
                    const granted = companyAccess.has(c.tenantId);
                    const canWrite = companyAccess.get(c.tenantId) ?? false;
                    return (
                      <div key={c.tenantId} className="px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <Building2 className="w-4 h-4 text-gray-400" /> {c.name}
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={granted}
                            aria-label={`${granted ? 'Revoke' : 'Grant'} access to ${c.name}`}
                            onClick={() => toggleCompany(c.tenantId)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${granted ? 'bg-blue-600' : 'bg-gray-200'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${granted ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        {granted && (
                          <div className="mt-2 flex gap-2 pl-6">
                            <button type="button" onClick={() => setCompanyWrite(c.tenantId, false)}
                              className={`px-3 py-1 text-xs font-medium rounded-full border ${!canWrite ? 'bg-gray-800 text-white border-gray-800' : 'text-gray-600 border-gray-300'}`}>
                              Read-only
                            </button>
                            <button type="button" onClick={() => setCompanyWrite(c.tenantId, true)}
                              className={`px-3 py-1 text-xs font-medium rounded-full border ${canWrite ? 'bg-blue-600 text-white border-blue-600' : 'text-gray-600 border-gray-300'}`}>
                              Read-write
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900">Modules</h3>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Which of your own modules this person can use in their granted companies.</p>
              {modules.length === 0 ? (
                <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">
                  You don't have access to any modules to grant yet.
                </p>
              ) : (
                <div className="space-y-4">
                  {groups.map((group) => (
                    <div key={group.key}>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1.5">{group.label}</p>
                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                        {group.items.map(renderModuleRow)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="px-5 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? 'Saving…' : initial ? 'Save Changes' : 'Create Team Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTeamMemberModal;
